import { Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { ConsultationBand, PublicHero, PublicShell } from "@/components/PublicShell"
import { PublicMeta } from "@/components/public/PublicMeta"
import { HOW_IT_WORKS_STEPS } from "@/components/public/how-it-works-steps"

const TITLE = "How Bespoke Wine Labels Work"
const DESCRIPTION =
  "Five steps from choosing your South African wines to your own bespoke label, approved by you and delivered anywhere in the UK. From 100 mixed bottles."

const ROMAN = ["I", "II", "III", "IV", "V"]

// Turnaround is an open input from Duncan (PRD open input #4): the copy below
// says so plainly rather than quoting a number we do not have.
const ASSURANCES = [
  {
    title: "From 100 bottles",
    body: "Your minimum is 100 bottles, mixed across reds, whites, rosé and MCC however you like.",
  },
  {
    title: "Approval before print",
    body: "You see and approve a proof of your label. Nothing is printed until you are entirely happy.",
  },
  {
    title: "Delivered UK-wide",
    body: "To your venue, office or home, anywhere in the United Kingdom.",
  },
  {
    title: "Your timeline",
    body: "Our typical turnaround is to be confirmed. Tell us your date and we will give you an honest timeline when we speak.",
  },
]

export default function HowItWorks() {
  return (
    <PublicShell>
      <PublicMeta title={TITLE} description={DESCRIPTION} />

      <PublicHero
        eyebrow="How it works"
        title={
          <>
            Five unhurried steps, <span className="soft">one label that is yours.</span>
          </>
        }
        lede="From the first conversation to a labelled case at your door, we guide you through the wines, the design and the delivery."
        actions={
          <Button asChild variant="gold" size="lg">
            <Link href="/contact">Request a Consultation</Link>
          </Button>
        }
      />

      <section className="band band-charcoal" aria-labelledby="process-title">
        <div className="shell">
          <div className="band-head">
            <div className="eyebrow">The process</div>
            <h2 id="process-title">From the Cape to your table</h2>
          </div>
          <ol className="process">
            {HOW_IT_WORKS_STEPS.map((step, index) => {
              const Icon = step.icon
              return (
                <li key={step.title} className="process-step">
                  <div className="process-head">
                    <span className="process-numeral" aria-hidden="true">
                      {ROMAN[index]}
                    </span>
                    <span className="process-icon" aria-hidden="true">
                      <Icon size={22} strokeWidth={1.25} />
                    </span>
                  </div>
                  <div className="process-body">
                    <h3>
                      <span className="sr-only">Step {index + 1}: </span>
                      {step.title}
                    </h3>
                    {step.detail.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                    {step.note && <span className="process-note">{step.note}</span>}
                  </div>
                </li>
              )
            })}
          </ol>
        </div>
      </section>

      <section className="band band-black" aria-labelledby="assurances-title">
        <div className="shell">
          <div className="band-head">
            <div className="eyebrow">Good to know</div>
            <h2 id="assurances-title">The details, plainly</h2>
          </div>
          <div className="assurances">
            {ASSURANCES.map((item) => (
              <div key={item.title} className="assurance">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band band-charcoal" aria-labelledby="begin-title">
        <div className="shell interlude">
          <div className="eyebrow eyebrow-center">Step one</div>
          <h2 id="begin-title">Begin with the wine</h2>
          <p className="lede">
            Browse wines from 50+ South African winemakers through our partnership with Sideways
            Wine, then tell us about your occasion.
          </p>
          <Button asChild variant="gold" size="lg">
            <Link href="/finder">Find your wine</Link>
          </Button>
        </div>
      </section>

      <ConsultationBand brochure />
    </PublicShell>
  )
}
